import { request } from "utils/api";
import { serialize } from "object-to-formdata";
import { Membership } from "./memberships";

// reduced user profile extended by various endpoints
export interface ReducedUserProfile {
    firstName: string,
    id: string,
    lastName: string,
    profilePictureUrl: string,
}

// reduced user profile with email
export interface ReducedUserProfileWithEmail extends ReducedUserProfile {
    email: string,
}

export type UserTypes = ("root-dev" | "root-management" | "root-marketing" | "root-sales" | "root-support" | "publisher" | "hero" | "company-profile")[];

// user profile from GET /auth/me
export interface UserProfile extends ReducedUserProfileWithEmail {
    birthday: string,
    gender: "male" | "female" | "diverse" | "none",
    isAlias: boolean,
    memberships: Membership[],
    onboardingStep: number,
    sportForms: string[],
    statusText: string,
    userTypes?: UserTypes,
}

// user settings
export interface UserSettings {
    activityEmail: boolean,
    activityPushNotification: boolean,
    allow3rdPartyCheckIns: boolean,
    bookingEmail: boolean,
    bookingPushNotification: boolean,
    competitionEmail: boolean,
    competitionPushNotification: boolean,
    followerEmail: boolean,
    followerPushNotification: boolean,
    forYouEmail: boolean,
    forYouPushNotification: boolean,
    isSubscribedToNewsletter: boolean,
    privacyLevel: "private" | "clubs-only" | "public" | "public-inviteable",
}

// user contact data
export interface ContactData {
    city: string,
    fullName: string,
    postalCode: string,
    street: string,
    telephoneCountryCode: string,
    telephoneNumber: string,
}

// fetch current user profile (GET /auth/me)
export const getProfile = () => request<UserProfile>({
    endpoint: "/auth/me",
    method: "get",
});

// update profile (PATCH /profile)
export const patchProfile = (data: {
    birthday?: string,
    firstName?: string,
    gender?: UserProfile["gender"],
    lastName?: string,
    sportForms?: string[],
    statusText?: string,
}) => request<UserProfile>({
    data,
    endpoint: "/profile",
    method: "patch",
});

// remove profile picture (DELETE /profile/picture)
export const deleteProfilePicture = () => request<UserProfile>({
    endpoint: "/profile/picture",
    method: "delete",
});

// update profile picture (PUT /profile/picture)
export const putProfilePicture = (data: {
    profilePicture: File,
}) => request<UserProfile>({
    data: serialize(data),
    endpoint: "/profile/picture",
    method: "put",
});

// fetch user settings (GET /settings)
export const getSettings = () => request<UserSettings>({
    endpoint: "/settings",
    method: "get",
});

// update user settings (PATCH /settings)
export const patchSettings = (data: {
    activityEmail?: boolean,
    activityPushNotification?: boolean,
    allow3rdPartyCheckIns?: boolean,
    bookingEmail?: boolean,
    bookingPushNotification?: boolean,
    followerEmail?: boolean,
    followerPushNotification?: boolean,
    isSubscribedToNewsletter?: boolean,
    onboardingStep?: number,
    privacyLevel?: "private" | "clubs-only" | "public" | "public-inviteable",
}) => request<UserSettings>({
    data,
    endpoint: "/settings",
    method: "patch",
});

// delete user account (DELETE /settings/account)
export const deleteAccount = () => request<{}>({
    endpoint: "/settings/account",
    method: "delete",
});

// request email change (PATCH /settings/change-email)
export const patchChangeEmail = (data: {
    newEmail: string,
}) => request<{}>({
    data,
    endpoint: "/settings/change-email",
    method: "patch",
});

// confirm email change (PATCH /settings/change-email/confirm)
export const patchConfirmChangeEmail = (data: {
    confirmationCode: string,
}) => request<UserProfile>({
    data,
    endpoint: "/settings/change-email/confirm",
    method: "patch",
});

// request data export (GET /settings/data-export)
export const getDataExport = () => request<{ url: string }>({
    endpoint: "/settings/data-export",
    method: "get",
});

// retrieve contact data (GET /profile/contact-data)
export const getProfileContactData = () => request<ContactData>({
    endpoint: "/profile/contact-data",
    method: "get",
});

// create or update contact data (PUT /profile/contact-data)
export const putProfileContactData = (data: ContactData) => request<ContactData>({
    data,
    endpoint: "/profile/contact-data",
    method: "put",
});
